<template>
  <div
    v-loading="isApplyingChanges"
    class="invite-manager-wrapper"
    element-loading-text="Saving the changes..."
  >
    <ElTabs
      v-model="activeRole"
      :stretch="true"
      @tab-click="resetSearch"
    >
      <ElTabPane
        label="Can Edit Boards in my Division"
        :name="userRoles.CREATOR"
      />
      <ElTabPane
        label="Can Just See Boards in my Division"
        :name="userRoles.REVIEWER"
      />
    </ElTabs>
    <div class="invite-manager">
      <div class="invite-manager__search">
        <ElInput
          ref="searchInput"
          v-model="searchText"
          placeholder="Search..."
          size="small"
          clearable
        >
          <i
            slot="prefix"
            class="el-input__icon el-icon-search"
          />
        </ElInput>
      </div>
      <ElForm
        ref="invitesToCreate"
        label-position="top"
        class="invite-manager__new-invite"
        :model="invitesToCreate"
        :rules="validationRules"
        hide-required-asterisk
      >
        <ElFormItem
          prop="inviteeList"
          class="new-invite"
        >
          <input
            ref="newInviteField"
            v-model.trim="editorEmail"
            class="new-invite__field"
            type="email"
            placeholder="e-mail address"
            @keydown.backspace.stop="removeLastInvitee"
            @keydown.enter.prevent.stop="tokenizeNewEmail"
            @keydown.space.prevent.stop="tokenizeNewEmail"
            @keydown.tab.prevent.stop="tokenizeNewEmail"
            @keydown.comma.prevent.stop="tokenizeNewEmail"
            @paste.prevent.stop="handlePaste"
          >
          <ElButton
            :disabled="!editorEmail"
            type="text"
            class="new-invite__button"
            @click="tokenizeNewEmail"
          >
            Add more
          </ElButton>
        </ElFormItem>
      </ElForm>
      <div
        v-if="invitesToCreateFilteredByRole.length > 0"
        class="invite-manager__pending-invites"
      >
        <ElTag
          v-for="(invite, inviteIndex) in invitesToCreateFilteredByRole"
          :key="inviteIndex"
          class="new-invite__tag"
          :type="invite.isValid ? 'info' : 'danger'"
          closable
          disable-transitions
          @close="removeInvitePending(invite.to_email)"
        >
          {{ invite.to_email }}
        </ElTag>
      </div>
      <div class="invites-list__invites-list">
        <template v-for="(invite, inviteIndex) in invitesFilteredByRole">
          <div
            v-if="shouldDisplayInvite(invite, user)"
            :key="inviteIndex"
            class="invites-list__invite"
          >
            <span class="invites-list__invite-email">{{
              invite.to_email
            }}</span>
            <ElButton
              v-if="invite.to_email !== user.email"
              class="invites-list__delete"
              @click="removeInvitedUser(invite)"
            >
              <i class="el-icon el-icon-close" />
            </ElButton>
          </div>
        </template>
      </div>
      <div class="invite-manager__footer">
        <span v-if="activeRole === userRoles.CREATOR">
          People in this list will be able to see and edit all the content in
          this division.
        </span>
        <span v-else>
          People in this list will be able to see but NOT edit the content in
          the division that is<br>set as "Visible".
        </span>
      </div>
    </div>
    <div class="invite-manager-wrapper__footer stitch-list-spaced-horizontal">
      <ElButton
        plain
        :disabled="isApplyingChanges"
        @click="dropChanges"
      >
        Cancel
      </ElButton>
      <ElPopover
        placement="top-start"
        trigger="manual"
        :value="showError"
        @show="schedulePopupClose"
      >
        <span v-if="showError">{{ error.message }}</span>
        <ElButton
          slot="reference"
          plain
          type="primary"
          :disabled="isApplyingChanges"
          @click="applyChanges"
        >
          Save
        </ElButton>
      </ElPopover>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'

import { userRoles } from '@/constants/user'
import Utils from '@/services/utils'

// eslint-disable-next-line no-useless-escape -- email regex from https://emailregex.com/
const REGEX_EMAIL = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/

export default {
  name: 'InviteManager',

  props: {
    groupId: {
      type: Number,
      required: true
    }
  },

  data () {
    return {
      showError: false,
      inviteeEmail: null,
      activeRole: null,
      userRoles: userRoles,
      editorEmail: '',
      searchText: '',
      invitesToCreate: {
        inviteeList: []
      },
      invitesToDelete: [],
      isApplyingChanges: false,
      timeoutPopupClose: 4000,
      validationRules: {
        inviteeList: [
          {
            validator: this.validatorInvitesToCreate
          }
        ]
      }
    }
  },

  computed: {
    ...mapGetters(['getInvites', 'getUser', 'getError']),

    /**
     * @returns {object}
     */
    error () {
      return this.getError()
    },

    /**
     * @returns {Array}
     */
    invites () {
      return this.getInvites()
    },

    /**
     * @returns {Array}
     */
    invitesToCreateFilteredByRole () {
      return this.invitesToCreate.inviteeList.filter(
        invite => invite.role === this.activeRole
      )
    },

    /**
     * @returns {Array}
     */
    invitesFilteredByRole () {
      const searchText = this.searchText

      const invitesToDisplay = this.invites
        .filter(invite => !this.isInviteToBeDeleted(invite))
        .sort((invite1, invite2) => invite1.id - invite2.id)

      return invitesToDisplay
        .filter(
          invite =>
            invite.role === this.activeRole &&
            invite.to_email.toLowerCase().includes(searchText.toLowerCase())
        )
        .reverse()
    },

    /**
     * @returns {object}
     */
    user () {
      return this.getUser()
    }
  },

  watch: {
    /**
     */
    async groupId () {
      await this.initializeInvites()
    },

    /**
     * @param {any} value
     */
    error (value) {
      if (value) {
        this.showError = true
      } else {
        this.showError = false
      }
    }
  },

  async created () {
    this.activeRole = userRoles.CREATOR

    await this.initializeInvites()
  },

  methods: {
    ...mapActions(['fetchInvites', 'createInvite', 'deleteInvite', 'setError']),

    /**
     */
    async initializeInvites () {
      await this.fetchInvites({ groupId: this.groupId })
    },

    /**
     * @param   {object}  invite
     *
     * @returns {boolean}
     */
    isInviteToBeDeleted (invite) {
      const index = this.invitesToDelete.findIndex(
        inviteToDelete => invite.to_email === inviteToDelete.to_email
      )

      return index > -1
    },

    /**
     * @param {Event} event
     */
    removeLastInvitee (event) {
      if (event.target.value === '') {
        event.preventDefault()

        const inviteeList = this.invitesToCreate.inviteeList

        if (inviteeList.length > 0) {
          this.invitesToCreate.inviteeList.pop()
          this.focusEmailField()
        }
      }
    },

    /**
     * @param {string} invitedUser
     */
    removeInvitedUser (invitedUser) {
      const invite = this.invites.find(
        invite => invite.to_email === invitedUser.to_email
      )

      if (invite) {
        this.invitesToDelete.push(invite)
      }
    },

    /**
     * @param {string} inviteeEmail
     */
    removeInvitePending (inviteeEmail) {
      const index = this.invitesToCreate.inviteeList.findIndex(
        invite => invite.to_email === inviteeEmail
      )

      if (index > -1) {
        this.invitesToCreate.inviteeList.splice(index, 1)
      }
    },

    /**
     */
    applyChanges () {
      this.$refs.invitesToCreate.validate(async isValid => {
        if (!isValid) {
          return
        }

        this.setError(null)
        this.isApplyingChanges = true

        try {
          await this.deleteInvites()
          await this.createInvites()

          this.clearInviteManager()
          this.closeInviteManager()
        } finally {
          this.isApplyingChanges = false
        }
      })
    },

    /**
     */
    async deleteInvites () {
      while (this.invitesToDelete.length > 0) {
        await this.deleteInvite({
          inviteId: this.invitesToDelete[0].id
        })

        this.invitesToDelete.shift()
      }
    },

    /**
     */
    async createInvites () {
      while (this.invitesToCreate.inviteeList.length > 0) {
        const invite = this.invitesToCreate.inviteeList[0]

        await this.createInvite({
          inviteeEmail: invite.to_email,
          groupId: this.groupId,
          role: invite.role
        })

        this.invitesToCreate.inviteeList.shift()
      }
    },

    /**
     */
    dropChanges () {
      this.$refs.invitesToCreate.clearValidate()
      this.editorEmail = ''
      this.showError = false
      this.setError(null)
      this.clearInviteManager()
      this.closeInviteManager()
    },

    /**
     */
    clearInviteManager () {
      this.invitesToCreate.inviteeList = []
      this.invitesToDelete = []
      this.inviteeEmail = null
    },

    /**
     */
    closeInviteManager () {
      this.$emit('close-invite-manager')
      this.$refs.invitesToCreate.clearValidate()
    },

    /**
     * @param   {object}  invite
     * @param   {object}  user
     *
     * @returns {boolean}
     */
    shouldDisplayInvite (invite, user) {
      if (Utils.isStitchEmail(user.email)) {
        return true
      } else {
        if (Utils.isStitchEmail(invite.to_email)) {
          return false
        } else {
          return true
        }
      }
    },

    /**
     */
    schedulePopupClose () {
      setTimeout(() => {
        this.showError = false
      }, this.timeoutPopupClose)
    },

    /**
     */
    validateInviteeListField () {
      this.$refs.invitesToCreate.validateField('inviteeList')
    },

    /**
     * @param {object}   rule
     * @param {object[]} value
     * @param {Function} callback
     */
    validatorInvitesToCreate (rule, value, callback) {
      const uniqueInvitees = {}

      value.forEach(invitee => {
        const email = invitee.to_email.toLowerCase()

        if (new RegExp(REGEX_EMAIL).test(email)) {
          invitee.isValid = true
        } else {
          invitee.isValid = false
        }

        if (uniqueInvitees[email]) {
          invitee.isValid = false
        } else {
          uniqueInvitees[email] = true
        }

        if (this.invitesFilteredByRole.find(user => user.to_email === email)) {
          invitee.isValid = false
        }
      })

      if (value.every(invitee => invitee.isValid)) {
        callback()
      } else {
        callback(
          new Error(
            'Please provide valid email addresses. No duplicates are allowed.'
          )
        )
      }
    },

    /**
     */
    tokenizeNewEmail () {
      const pendingInput = this.editorEmail

      if (pendingInput !== '') {
        this.invitesToCreate.inviteeList.push({
          to_email: pendingInput,
          groupId: this.groupId,
          role: this.activeRole,
          isValid: true
        })
        this.editorEmail = ''
      }

      this.validateInviteeListField()
      this.focusEmailField()
    },

    /**
     * @param {ClipboardEvent} pasteEvent
     */
    handlePaste (pasteEvent) {
      const newInvitees = this.splitPasteContent(
        pasteEvent.clipboardData.getData('Text')
      ).map(email => ({
        to_email: email,
        role: this.activeRole,
        isValid: true
      }))

      this.invitesToCreate.inviteeList.push(...newInvitees)
      this.validateInviteeListField()
      this.focusEmailField()
    },

    /**
     * @param   {string}   pasteContent
     *
     * @returns {string[]}
     */
    splitPasteContent (pasteContent) {
      let parts

      if (!pasteContent) {
        return []
      }

      const pasteContentTrimmed = pasteContent.trim()
      const regexes = [
        /,\s*/g, // comma with optional spaces after
        /\s+/g // spaces
      ]

      for (const regex of regexes) {
        const trimParts = pasteContentTrimmed
          .split(regex)
          .filter(part => part.length > 0)
        parts = trimParts.map(email => email.trim())

        if (parts.length > 0 && parts[0] !== pasteContentTrimmed) {
          return parts
        }
      }

      return parts
    },

    /**
     */
    focusEmailField () {
      this.$nextTick().then(() => {
        this.$refs.newInviteField.focus()
      })
    },

    /**
     */
    resetSearch () {
      this.searchText = ''
    }
  }
}
</script>

<style lang="scss" scoped>
$max-width-email: 60%;
$inner-scroll-height: 40vh;

.invite-manager-wrapper__footer {
  display: flex;
  justify-content: center;
}

.invite-manager {
  @include scroll-y-smooth();

  width: 100%;
  margin-bottom: $spacer-unit-x2;
}

.invite-manager__search {
  margin-bottom: $spacer-unit;
}

.invite-manager__new-invite {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: $spacer-unit;
}

.invite-manager__pending-invites {
  padding-top: $spacer-unit;
}

.invites-list__invites-list {
  @include scroll-y-smooth();

  width: 100%;
  max-height: $inner-scroll-height;
  margin-bottom: $spacer-unit;
  border-top: $border-thin-grey-extra-light;
}

.invites-list__invite {
  display: flex;
  justify-content: space-between;
  padding: $spacer-unit-x2 0;
  color: $grey;
  border-bottom: $border-thin-grey-extra-light;

  .invites-list__invite-email {
    @include text-ellipsis($max-width-email);
  }

  .invites-list__delete {
    display: none;
    padding: 0;
    color: $red;
    font-size: $font-size-large;
    background: none;
    border: 0;
  }

  &:hover {
    .invites-list__delete {
      display: block;
    }
  }
}

.invite-manager__footer {
  font-size: $font-size-medium;
  word-break: break-word;
}

.new-invite {
  width: 100%;

  /deep/ .el-form-item__content {
    display: flex;
  }
}

.new-invite__tag {
  margin-right: $spacer-unit;
  margin-bottom: $spacer-unit;
}

.new-invite__field {
  width: 100%;
  padding: $spacer-unit;
  text-align: left;
}
</style>
